import {
	Component,
	inject,
	viewChild,
	computed,
	signal,
	OnInit,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { PdfModalComponent } from '@components/pdf-modal/pdf-modal.component';
import { MatterFileTypeStrings } from '@core/util/file';
import { ICON } from '@core/util/icon';
import { MortgageSourceTag } from '@core/util/tags';
import { MatterFile, MortgageDocumentSource } from '@models/matter-file';
import { select, dispatch } from '@ngxs/store';
import { FileDownloadType } from '@state/file-download/fileDownload.model';
import { MergedUploadedMatterDocumentSelectors } from '@state/global/uploaded-documents/upload-documents';
import { SelectedMatter } from 'src/app/shared/state/matter/matter.selectors';
import * as MatterUploadedFilesActions from '@state/matter/uploaded-files/matter-uploaded-files.actions';
import { DrawerService } from '../../../../ui/drawer/drawer.service';
import {
	UploadedFileEditModalComponent,
	UploadedFileEditModalDialogData,
} from './edit-modal/edit-modal.component';
import {
	UploadDocumentModalComponent,
	UploadModalDocumentData,
} from './modal/modal.component';
import { FileDownloadActions } from 'src/app/shared/state/file-download/fileDownload.index';
import { ActivatedRoute } from '@angular/router';

export enum UploadedDocumentsOpenQueryParam {
	UPLOAD = 'uploadModal',
}

@Component({
	selector: 'app-uploaded',
	templateUrl: './uploaded.component.html',
	host: { class: 'block p-6' },
	standalone: false,
})
export class UploadedDocumentsComponent implements OnInit {
	private sort = viewChild(MatSort);
	private matterId_ = select(SelectedMatter.id);
	private allDocuments_ = select(
		MergedUploadedMatterDocumentSelectors.getUploadedDocs,
	);
	private docDataMapped_ = computed(() =>
		this.allDocuments_()
			.map(this.buildItem)
			.sort((a, b) => b.uploadedAt - a.uploadedAt),
	);
	private drawerService = inject(DrawerService);
	private matDialog = inject(MatDialog);
	private filter_ = signal('');
	private downloadFile = dispatch(FileDownloadActions.DownloadFile);
	private deleteUploadedFile = dispatch(MatterUploadedFilesActions.Delete);
	private activatedRoute = inject(ActivatedRoute);
	public files: MatterFile[];
	public ICON = ICON;
	public displayedColumns = ['name', 'type', 'uploadedAt', 'delete'];
	public envelopeDisplayColumns = [
		'matterId',
		'type',
		'Document',
		'SignerDetails',
	];
	public dataSource_ = computed(() => {
		const dataSource = new MatTableDataSource(this.docDataMapped_());
		const sort = this.sort();
		const filter = this.filter_();
		if (sort) {
			dataSource.sort = sort;
		}
		if (filter) {
			dataSource.filter = filter;
		}

		return dataSource;
	});

	ngOnInit(): void {
		const openQueryParam =
			this.activatedRoute.snapshot.queryParamMap.get('open');
		if (openQueryParam === UploadedDocumentsOpenQueryParam.UPLOAD) {
			this.upload();
		}
	}

	trackByUid = (index: number, item: MatterFile): string => item.id;

	edit(matter: MatterFile) {
		// Check if rename is allowed
		if (this.hasKeyword(matter, 'system:no_rename')) {
			return;
		}

		const data: UploadedFileEditModalDialogData = {
			file: matter,
		};
		this.matDialog.open(UploadedFileEditModalComponent, {
			data,
			width: '80vw',
			maxWidth: '800px',
		});
	}

	upload(): void {
		const data: UploadModalDocumentData = {
			matterId: this.matterId_(),
			runAiScraping: false,
		};
		this.matDialog.open(UploadDocumentModalComponent, {
			data,
			width: '80vw',
			maxWidth: '800px',
		});
	}

	download(file: MatterFile): void {
		this.downloadFile({
			name: file.name,
			mime: file.fileType,
			data: file.url,
			dataType: FileDownloadType.GOOGLE_URL,
		});
	}

	openFile(file: MatterFile): void {
		this.drawerService.openDrawer(PdfModalComponent, {
			title: file.name,
			noStyle: true,
			data: {
				googleSrcUrl: file.url,
				fileName: file.name,
				fileType: file.fileType,
			},
		});
	}

	remove(id: string): void {
		// Find the file to check keywords
		const file = this.docDataMapped_().find((f) => f.id === id);
		if (file && this.hasKeyword(file, 'system:no_delete')) {
			return;
		}

		this.deleteUploadedFile(id);
	}

	applySearch(filterValue: string): void {
		this.filter_.set(filterValue);
	}

	private isSyncMortgageInstruction = (file: MatterFile) =>
		file?.systemTags?.includes(
			MortgageSourceTag(MortgageDocumentSource.LENDER),
		);

	private hasKeyword = (file: MatterFile, keyword: string): boolean =>
		file?.keywords?.includes(keyword) ?? false;

	private buildItem = (file: MatterFile) => ({
		...file,
		friendlyType: MatterFileTypeStrings[file.type] ?? file.type,
		allowDelete:
			!this.isSyncMortgageInstruction(file) &&
			!this.hasKeyword(file, 'system:no_delete'),
		allowRename: !this.hasKeyword(file, 'system:no_rename'),
	});
}
